import { AssistantOutlined, KeyboardArrowDown } from "@mui/icons-material";
import { Button, IconButton } from "@mui/material";

import { useState } from "react";
import ChatSettingsTooltip from "src/reusable/components/ChatSettingsTooltip";
import { truncateText } from "src/reusable/Functions";
import {
  useActivePersonaIdentity,
  useChatStore,
} from "src/views/assistant/stores/ChatStore";
import { PersonaAPIData } from "src/views/assistant/types";
import PersonaSelectorModal from "./PersonaSelectorModal";

type ChatStyleSelectionButtonProps = {
  onlyIcon?: boolean;
  // When set, the selector only offers personas with this toolkit active.
  requiredToolkitName?: string;
};

const ChatPersonaSelectButton = ({
  onlyIcon = false,
  requiredToolkitName,
}: ChatStyleSelectionButtonProps) => {
  const [modalOpen, setModalOpen] = useState(false);

  // Get selected persona from unified store - completely store-only
  const {
    config: chatConfig,
    persona,
    setConfig,
    setPersona,
    setRouting,
  } = useChatStore();
  const identity = useActivePersonaIdentity();

  // No URL handling - purely store-driven component
  // URL sync handled by parent page components only
  const selectedPersona = identity.slug || null;

  const setChatConfig = (config: any) => {
    // Set operational config (model, datasets, etc.) without identity fields
    setConfig({
      modelId: config.modelId,
      modelVersionNum: config.modelVersionNum,
      initialPrompt: config.initialPrompt || "",
      datasets: config.datasets || [],
      chatControlMetadata: config.chatControlMetadata || {},
      topSearchResults: config.topSearchResults,
      toolCalling: config.toolCalling,
      chatConfigId: config.chatConfigId,
      modelCapabilities: config.modelCapabilities,
    });
    // Identity lives in persona only
    if (config.name && config.slug) {
      setPersona({
        id: config.chatConfigId,
        slug: config.slug,
        name: config.name,
        description: config.description || "",
        initialPrompt: config.initialPrompt || "",
        systemPrompt: config.systemPrompt || "",
        datasets: config.datasets || [],
        modelCapabilities: config.modelCapabilities,
      });
    }
  };

  const clearChatConfig = () => {
    // Don't set config to null - maintain base config but clear persona-specific settings
    const currentConfig = useChatStore.getState().config;
    if (currentConfig) {
      setConfig({
        modelId: currentConfig.modelId,
        modelVersionNum: currentConfig.modelVersionNum,
        datasets: [],
        chatControlMetadata: {},
        topSearchResults: 5,
        toolCalling: true,
        chatConfigId: undefined,
      });
    }
    setPersona(null);
  };

  const handleClick = () => {
    setModalOpen(true);
  };

  const handleConfirm = (selectedPersonaData: PersonaAPIData | null) => {
    if (!selectedPersonaData) {
      setRouting(null);
      clearChatConfig();
      return;
    }

    // Extract model capabilities from model_version.inference_data_config
    const inferenceConfig =
      selectedPersonaData?.model_version?.inference_data_config;
    const modelCapabilities =
      inferenceConfig && typeof inferenceConfig === "object"
        ? {
            supportsVision:
              (inferenceConfig as Record<string, unknown>).supports_vision ===
              true,
            supportsReasoning:
              (inferenceConfig as Record<string, unknown>)
                .supports_reasoning === true,
            supportsFunctionCalling:
              (inferenceConfig as Record<string, unknown>)
                .supports_function_calling === true,
          }
        : undefined;

    // Clear routing metadata — manual selection overrides auto-routing
    setRouting(null);

    setChatConfig({
      name: selectedPersonaData?.name || "",
      slug: selectedPersonaData?.slug || "",
      description: selectedPersonaData?.description || "",
      initialPrompt: selectedPersonaData?.initial_prompt || "",
      systemPrompt: selectedPersonaData?.system_prompt || "",
      modelId:
        selectedPersonaData?.config_data?.model_id ??
        chatConfig?.modelId ??
        null,
      modelVersionNum:
        selectedPersonaData?.model_version?.version_number ??
        chatConfig?.modelVersionNum ??
        null,
      datasets: selectedPersonaData?.datasets || [],
      chatControlMetadata: {},
      topSearchResults:
        selectedPersonaData?.config_data?.top_search_results || 5,
      toolCalling: true,
      chatConfigId: selectedPersonaData?.id,
      modelCapabilities,
    });

    // URL updates handled by parent page components
  };

  return (
    <>
      <ChatSettingsTooltip
        title="Select an agent to customize AtomAssist's behavior and capabilities."
        placement="top"
        enterDelay={200}
        arrow
      >
        {onlyIcon ? (
          <IconButton
            color={selectedPersona ? "primary" : "inherit"}
            aria-label="Select chat agent"
            onClick={handleClick}
            data-testid="aa-persona-selector-btn"
            data-attr="assistant:btn:open-agent-selector"
          >
            <AssistantOutlined />
          </IconButton>
        ) : (
          <Button
            aria-label="Select chat agent"
            variant="text"
            size="small"
            color={selectedPersona ? "primary" : "inherit"}
            onClick={handleClick}
            startIcon={<AssistantOutlined />}
            endIcon={<KeyboardArrowDown />}
            sx={{ textTransform: "capitalize" }}
            data-testid="aa-persona-selector-btn"
            data-attr="assistant:btn:open-agent-selector"
          >
            {selectedPersona
              ? truncateText(persona?.name || identity.name || "Agent", 25)
              : "Select Agent"}
          </Button>
        )}
      </ChatSettingsTooltip>

      <PersonaSelectorModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onConfirm={handleConfirm}
        requiredToolkitName={requiredToolkitName}
        selectedPersona={
          persona
            ? ({
                id: persona.id,
                slug: persona.slug,
                name: persona.name,
                description: persona.description,
              } as PersonaAPIData)
            : null
        }
      />
    </>
  );
};

export default ChatPersonaSelectButton;
